// Account Manager dashboard (Fitness), /dashboard/am. Staff look, current app
// style. The home page for users whose job role is account_manager_fitness.
//
// DATA: every panel is its own query, so one slow or failed panel never blanks
// the page. The spine-based panels (alerts, glance, high CPL, movers, pacing)
// reuse the Advertising Home overview read (same query key, shared cache) and
// the pure cores in src/lib/amDashboard and src/lib/advertisingHome. Nothing here
// computes a CPL itself: the "spending, no leads" rule lives in noLeads.ts.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../components/layout/PageHeader';
import { SelectField } from '../../components/ui/SelectField';
import { useCurrentUser } from '../auth';
import { ApiError } from '../../lib/api';
import { completeDayBound, evaluateAlerts, type HomeAlert } from '../../lib/advertisingHome/alerts';
import { applyAcknowledgements } from '../../lib/advertisingHome/acknowledgements';
import { BrandAverages } from '../../lib/advertisingHome/noLeads';
import { filterRowsToAccounts } from '../../lib/advertisingHome/bookFilter';
import { addDaysYmd, indexSpine } from '../../lib/advertisingHome/spine';
import { buildHighCpl } from '../../lib/amDashboard/highCpl';
import { buildMovers } from '../../lib/amDashboard/movers';
import { buildPace, type PacePeriod, type PaceTab } from '../../lib/amDashboard/pace';
import { buildGlance, connectionOf, type GlanceRow } from '../../lib/amDashboard/glance';
import {
  acknowledgeAlert,
  listAcknowledgements,
  listAlertNotes,
} from '../advertisingHome/api';
import { getHomeOverview } from '../advertisingHome/api';
import {
  getAmAssets,
  getAmBrandWindows,
  getAmCampaigns,
  getAmContext,
  getAmPeople,
  getAmTopAds,
} from './api';
import {
  AlertsAndNotes,
  AssetsGallery,
  CampaignsTabs,
  GlanceTable,
  HighCplTable,
  MoversGrid,
  NeedsTiles,
  PaceSection,
  Panel,
  PeopleBlock,
  TopAdsGrid,
  type CampTab,
  type NeedTile,
} from './AmSections';

const ALL = '';

function greeting(now: Date): string {
  const h = now.getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

export function AmDashboardPage(): React.JSX.Element {
  const user = useCurrentUser();
  const [params] = useSearchParams();
  const am = params.get('am') ?? undefined;
  const scope = am ?? 'mine';
  const qc = useQueryClient();
  const [utcToday] = useState(() => new Date().toISOString().slice(0, 10));

  const [accountFilter, setAccountFilter] = useState(ALL);
  const [brandFilter, setBrandFilter] = useState(ALL);
  const [campTab, setCampTab] = useState<CampTab>('ending');
  const [adDays, setAdDays] = useState<7 | 30 | 90>(30);
  const [pacePeriod, setPacePeriod] = useState<PacePeriod>('this_month');
  const [paceTab, setPaceTab] = useState<PaceTab>('combined');

  const stale = 5 * 60_000;
  const ctx = useQuery({ queryKey: ['am-context', am], queryFn: () => getAmContext(am), staleTime: stale, retry: false });
  const overview = useQuery({ queryKey: ['advertising-home-overview', scope], queryFn: () => getHomeOverview(scope), staleTime: stale });
  const bw30 = useQuery({ queryKey: ['am-brand-windows', am, 30], queryFn: () => getAmBrandWindows(30, am), staleTime: stale });
  const campaigns = useQuery({ queryKey: ['am-campaigns', am], queryFn: () => getAmCampaigns(am), staleTime: stale });
  const assets = useQuery({ queryKey: ['am-assets', am], queryFn: () => getAmAssets(am), staleTime: 60_000 });
  const people = useQuery({ queryKey: ['am-people', am], queryFn: () => getAmPeople(am), staleTime: 60_000 });
  const topAds = useQuery({ queryKey: ['am-top-ads', am, adDays], queryFn: () => getAmTopAds(adDays, am), staleTime: stale });
  const acks = useQuery({ queryKey: ['advertising-home-acks'], queryFn: listAcknowledgements, staleTime: 60_000 });
  const notes = useQuery({ queryKey: ['advertising-home-alert-notes'], queryFn: listAlertNotes, staleTime: 60_000 });
  const ack = useMutation({
    mutationFn: acknowledgeAlert,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['advertising-home-acks'] }),
  });

  const ov = overview.data;
  const allAccounts = ov?.accounts;
  const accounts = useMemo(
    () =>
      (allAccounts ?? []).filter(
        (a) => (accountFilter === ALL || a.id === accountFilter) && (brandFilter === ALL || a.brandAccountId === brandFilter),
      ),
    [allAccounts, accountFilter, brandFilter],
  );
  const ids = useMemo(() => new Set(accounts.map((a) => a.id)), [accounts]);
  const rows = useMemo(() => filterRowsToAccounts(ov?.rows ?? [], accounts), [ov, accounts]);

  const lastDay = useMemo(
    () => ({
      meta: completeDayBound(ov?.asOf.meta ?? null, utcToday),
      google: completeDayBound(ov?.asOf.google ?? null, utcToday),
    }),
    [ov, utcToday],
  );
  const lastAny = [lastDay.meta, lastDay.google].filter((d): d is string => d !== null).sort()[0] ?? null;

  const brandAvg7 = useMemo(() => new BrandAverages(ov?.brandWindows), [ov]);
  const brandAvg30 = useMemo(() => new BrandAverages(bw30.data), [bw30.data]);

  const evaluation = useMemo(
    () =>
      evaluateAlerts({
        accounts,
        spine: indexSpine(rows),
        asOf: ov?.asOf ?? { meta: null, google: null },
        utcToday,
        includePacing: false,
        brandAverages: brandAvg7,
      }),
    [accounts, rows, ov, utcToday, brandAvg7],
  );
  const alertSplit = useMemo(
    () => applyAcknowledgements(evaluation.alerts, acks.data ?? [], Date.now(), 'am'),
    [evaluation, acks.data],
  );
  const teamNotes = useMemo(
    () =>
      (notes.data ?? [])
        .filter((n) => ids.has(n.accountId))
        .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
    [notes.data, ids],
  );

  const nameOf = (id: string): string => accounts.find((a) => a.id === id)?.name ?? id;

  const highCpl = useMemo(() => {
    const mk = (channel: 'meta' | 'google') => {
      const to = lastDay[channel];
      return to
        ? buildHighCpl({ accounts, rows, channel, from: addDaysYmd(to, -29), to, brandAverages: brandAvg30 })
        : [];
    };
    return { meta: mk('meta'), google: mk('google') };
  }, [accounts, rows, lastDay, brandAvg30]);

  const movers = useMemo(
    () => ({
      meta: buildMovers({ accounts, rows, channel: 'meta', lastDay: lastDay.meta }),
      google: buildMovers({ accounts, rows, channel: 'google', lastDay: lastDay.google }),
    }),
    [accounts, rows, lastDay],
  );

  const pace = useMemo(
    () => buildPace({ accounts, rows, period: pacePeriod, tab: paceTab, lastDay }),
    [accounts, rows, pacePeriod, paceTab, lastDay],
  );
  const monthPace = useMemo(() => {
    const per = (tab: PaceTab) => buildPace({ accounts, rows, period: 'this_month', tab, lastDay });
    return { combined: per('combined'), meta: per('meta'), google: per('google') };
  }, [accounts, rows, lastDay]);

  const connections = useMemo(() => new Map((ctx.data?.accounts ?? []).map((a) => [a.id, a])), [ctx.data]);

  const glance = useMemo(() => {
    if (!lastAny) return [];
    const open = new Map<string, number>();
    for (const a of alertSplit.visible) open.set(a.accountId, (open.get(a.accountId) ?? 0) + 1);
    const paceBy = new Map<string, { meta: GlanceRow['metaPace']; google: GlanceRow['googlePace'] }>();
    for (const a of accounts) {
      const m = monthPace.meta.rows.find((r) => r.accountId === a.id);
      const g = monthPace.google.rows.find((r) => r.accountId === a.id);
      paceBy.set(a.id, {
        meta: m ? { pace: m.pace, status: m.status } : null,
        google: g ? { pace: g.pace, status: g.status } : null,
      });
    }
    return buildGlance({
      accounts,
      rows,
      lastDay: lastAny,
      healthByAccount: evaluation.healthByAccount,
      openAlertsByAccount: open,
      connections,
      paceByAccount: paceBy,
      brandAverages: brandAvg7,
    });
  }, [accounts, rows, lastAny, alertSplit, evaluation, connections, monthPace, brandAvg7]);

  const inFilter = <T extends { accountId: string }>(list: T[] | undefined): T[] => (list ?? []).filter((i) => ids.has(i.accountId));
  const campData = campaigns.data
    ? {
        ...campaigns.data,
        endingSoon: inFilter(campaigns.data.endingSoon),
        pausedUnexpectedly: inFilter(campaigns.data.pausedUnexpectedly),
        rejectedAds: inFilter(campaigns.data.rejectedAds),
        waitingToPublish: inFilter(campaigns.data.waitingToPublish),
      }
    : undefined;

  const tiles: NeedTile[] = [
    {
      key: 'nolead',
      label: 'Spending, no leads (7 days)',
      count: ov ? new Set(evaluation.alerts.filter((a) => a.type === 'spend_no_leads').map((a) => a.accountId)).size : null,
      target: 'sec-high-meta',
      tone: 'red',
    },
    {
      key: 'conn',
      label: 'Broken connections',
      count: ctx.data ? accounts.filter((a) => !connectionOf(connections.get(a.id)).ok).length : null,
      target: 'sec-glance',
      tone: 'red',
    },
    { key: 'rejected', label: 'Rejected ads', count: campData ? campData.rejectedAds.length : null, target: 'sec-campaigns', tone: 'red' },
    {
      key: 'highcpl',
      label: 'High CPL',
      count: bw30.data ? new Set([...highCpl.meta, ...highCpl.google].map((r) => r.accountId)).size : null,
      target: 'sec-high-meta',
      tone: 'amber',
    },
    {
      key: 'pace',
      label: 'Off pace',
      count: ov ? monthPace.combined.rows.filter((r) => r.status !== null && r.status !== 'green').length : null,
      target: 'sec-pace',
      tone: 'amber',
    },
    { key: 'ending', label: 'Ending soon', count: campData ? campData.endingSoon.length : null, target: 'sec-campaigns', tone: 'amber' },
    { key: 'assets', label: 'New client assets', count: assets.data ? assets.data.newSinceYesterday : null, target: 'sec-assets', tone: 'neutral' },
  ];
  const jump = (t: NeedTile): void => {
    if (t.key === 'rejected') setCampTab('rejected');
    if (t.key === 'ending') setCampTab('ending');
    document.getElementById(t.target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const accountOptions = [{ value: ALL, label: 'All my accounts' }, ...(allAccounts ?? []).map((a) => ({ value: a.id, label: a.name }))];
  const brandOptions = [
    { value: ALL, label: 'All brands' },
    ...(ctx.data?.brandCounts ?? []).filter((b) => b.brandAccountId).map((b) => ({ value: b.brandAccountId as string, label: b.brandName })),
  ];
  if (ctx.error instanceof ApiError && ctx.error.status === 403) {
    return (
      <div>
        <PageHeader title="Account manager dashboard" />
        <p className="text-sm text-red-700">Only admins can view another account manager&apos;s dashboard.</p>
        <Link to="/dashboard/am" className="text-sm underline">Go to my dashboard</Link>
      </div>
    );
  }
  const now = new Date();
  const brandLine = (ctx.data?.brandCounts ?? []).map((b) => `${b.count} ${b.brandName}`).join(' · ');

  return (
    <div className="space-y-4">
      <PageHeader
        title={`${greeting(now)}${ctx.data?.firstName ? `, ${ctx.data.firstName}` : ''}`}
        description={`${now.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })} · ${accounts.length} accounts${brandLine ? ` · ${brandLine}` : ''}`}
        actions={
          <div className="flex gap-2">
            <div className="w-56"><SelectField value={accountFilter} onValueChange={setAccountFilter} options={accountOptions} aria-label="Account" /></div>
            <div className="w-44"><SelectField value={brandFilter} onValueChange={setBrandFilter} options={brandOptions} aria-label="Brand" /></div>
          </div>
        }
      />
      {!ctx.data?.isOwn && ctx.data && (
        <p className="rounded border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900">
          Viewing {ctx.data.firstName ? `${ctx.data.firstName}'s` : 'the whole agency’s'} dashboard.
        </p>
      )}

      <section aria-label="Needs you today">
        <h2 className="mb-2 text-sm font-semibold">Needs you today</h2>
        <NeedsTiles tiles={tiles} onJump={jump} />
      </section>

      <Panel id="sec-alerts" title="Alerts and team notes" state={{ data: ov ? true : undefined, isLoading: overview.isLoading, isError: overview.isError, refetch: overview.refetch }}>
        {() => (
          <AlertsAndNotes
            alerts={alertSplit.visible as HomeAlert[]}
            notes={teamNotes}
            nameOf={nameOf}
            pending={ack.isPending}
            onAck={(a) => ack.mutate({ accountId: a.accountId, channel: a.channel, alertType: a.type, surface: 'am', severity: a.severity })}
          />
        )}
      </Panel>

      <Panel id="sec-assets" title="Recently added by your clients" state={assets}>
        {(d) => <AssetsGallery data={d} amUid={ctx.data?.amUid ?? null} nowMs={Date.now()} />}
      </Panel>

      <Panel id="sec-glance" title="Accounts at a glance" state={{ data: ov ? true : undefined, isLoading: overview.isLoading, isError: overview.isError, refetch: overview.refetch }}>
        {() => <GlanceTable rows={glance} />}
      </Panel>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Panel id="sec-campaigns" title="Campaigns needing attention" state={{ data: campData, isLoading: campaigns.isLoading, isError: campaigns.isError, refetch: campaigns.refetch }}>
            {(d) => <CampaignsTabs data={d} tab={campTab} onTab={setCampTab} />}
          </Panel>
        </div>
        <Panel id="sec-people" title="Leads and client portal" state={people}>
          {(d) => <PeopleBlock data={d} />}
        </Panel>
      </div>

      <Panel id="sec-high-meta" title="High CPL · Meta (last 30 days)" state={{ data: bw30.data ? true : undefined, isLoading: bw30.isLoading || overview.isLoading, isError: bw30.isError, refetch: bw30.refetch }}>
        {() => <HighCplTable rows={highCpl.meta} />}
      </Panel>
      <Panel id="sec-high-google" title="High CPL · Google (last 30 days)" state={{ data: bw30.data ? true : undefined, isLoading: bw30.isLoading || overview.isLoading, isError: bw30.isError, refetch: bw30.refetch }}>
        {() => <HighCplTable rows={highCpl.google} />}
      </Panel>

      <Panel id="sec-movers" title="Movers" state={{ data: ov ? true : undefined, isLoading: overview.isLoading, isError: overview.isError, refetch: overview.refetch }}>
        {() => <MoversGrid meta={movers.meta} google={movers.google} />}
      </Panel>

      <Panel
        id="sec-top-ads"
        title="Top 10 Meta ads by cost per lead"
        state={topAds}
        right={
          <div className="w-32">
            <SelectField
              value={String(adDays)}
              onValueChange={(v) => setAdDays(Number(v) as 7 | 30 | 90)}
              options={[{ value: '7', label: 'Last 7 days' }, { value: '30', label: 'Last 30 days' }, { value: '90', label: 'Last 90 days' }]}
              aria-label="Period"
            />
          </div>
        }
      >
        {(d) => <TopAdsGrid data={d} />}
      </Panel>

      <Panel id="sec-pace" title="Budget pacing" state={{ data: ov ? true : undefined, isLoading: overview.isLoading, isError: overview.isError, refetch: overview.refetch }}>
        {() => <PaceSection result={pace} period={pacePeriod} tab={paceTab} onPeriod={setPacePeriod} onTab={setPaceTab} />}
      </Panel>
      {user.jobRole !== 'account_manager_fitness' && (
        <p className="text-[11px] text-muted-foreground">This dashboard is built for Fitness account managers.</p>
      )}
    </div>
  );
}
